import { DragEvent, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AcademicCapIcon,
  Bars2Icon,
  BookmarkIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentIcon,
  FolderIcon,
  PencilSquareIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useUser } from '@clerk/clerk-react';
import BloodDropIcon from '../brand/BloodDropIcon';
import PlaceLink from './PlaceLink';
import { MY_ORBCON_LABEL, MY_ORBCON_PANEL_ID, useUserSidebar } from '../../hooks/useUserSidebar';
import { useSavedItems } from '../../hooks/useSavedItems';
import { LOCAL_DEFAULT_FOLDER_ID, SavedFolder, SavedItem, placeholderDefaultFolder } from '../../types/savedItems';
import { capturePageFrom } from '../../utils/pageFrom';
import { hubPlaceHref } from '../../utils/hubChapter';
import { hubAPI } from '../../services/hubApi';
import { Hub } from '../../types/hub';
import { useRecentResources } from '../../hooks/useRecentResources';
import { getPostMetadata } from '../../services/mockMetadata';
import { contentTypeConfig } from '../../utils/metadataHelpers';
import { decodeHtmlEntities, stripHtml } from '../../utils/htmlEntities';
import { formatUserDisplayName, readProfileExtras } from '../../types/userProfile';
import { buildResourcePath } from '../../utils/resourceLinks';
import { elearningAPI } from '../../services/elearningApi';
import { Enrollment, EnrollmentStatus } from '../../types/elearning';
import { courseFace, courseFact, courseStatus } from '../../utils/cardFacts';
import { useResourceCurrency } from '../../hooks/useResourceCurrency';
import RevisionNotice from '../resources/RevisionNotice';
import { isUnderRevision } from '../../utils/resourceRevision';
import { format, parseISO } from 'date-fns';

const RECENT_LIMIT = 5;

function PanelSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Icon className="h-5 w-5 flex-shrink-0 text-accent" />
        {title}
      </h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function QuietLine({ children }: { children: ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

const LIST_ROW =
  'flex items-start gap-2 min-h-[44px] py-2 px-2 -mx-2 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:text-primary focus-ring';

function ContentTypeIcon({ id, title }: { id: number; title: string }) {
  const metadata = getPostMetadata({ id, title: { rendered: title } });
  const Icon = metadata?.contentType
    ? contentTypeConfig[metadata.contentType].icon
    : DocumentIcon;

  return (
    <Icon
      className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
        metadata?.contentType ? 'text-primary' : 'text-gray-400'
      }`}
      aria-hidden
    />
  );
}

function ItemIcon({ item }: { item: SavedItem }) {
  if (item.type === 'HUB') {
    return <FolderIcon className="h-4 w-4 flex-shrink-0 text-primary mt-0.5" aria-hidden />;
  }
  if (item.type === 'PROGRAM') {
    return <RectangleStackIcon className="h-4 w-4 flex-shrink-0 text-primary mt-0.5" aria-hidden />;
  }
  if (item.postId == null) {
    return <DocumentIcon className="h-4 w-4 flex-shrink-0 text-gray-400 mt-0.5" aria-hidden />;
  }
  return <ContentTypeIcon id={item.postId} title={item.title} />;
}

const SAVED_ITEM_ROW =
  'flex items-start gap-2 min-h-[44px] w-full py-2 px-2 rounded-md text-sm text-gray-700 group-hover/item:text-primary focus-ring';

const CLUSTER_FADE =
  'before:pointer-events-none before:absolute before:inset-y-0 before:right-full before:w-8 before:bg-gradient-to-r before:from-transparent';

const FOLDER_CLUSTER =
  `pointer-events-none absolute right-0 inset-y-0 z-10 flex items-center bg-white pl-2 transition-opacity duration-150 ease-in-out motion-reduce:transition-none opacity-0 group-hover/folder:bg-gray-50 group-hover/folder:opacity-100 group-focus-within/folder:bg-gray-50 group-focus-within/folder:opacity-100 ${CLUSTER_FADE} before:to-white group-hover/folder:before:to-gray-50 group-focus-within/folder:before:to-gray-50`;

const ITEM_CLUSTER =
  `pointer-events-none absolute right-0 inset-y-0 z-10 flex items-center bg-white pl-2 transition-opacity duration-150 ease-in-out motion-reduce:transition-none opacity-0 group-hover/item:bg-gray-50 group-hover/item:opacity-100 group-focus-within/item:bg-gray-50 group-focus-within/item:opacity-100 ${CLUSTER_FADE} before:to-white group-hover/item:before:to-gray-50 group-focus-within/item:before:to-gray-50`;

const FOLDER_HIT =
  'pointer-events-none inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md group-hover/folder:pointer-events-auto group-focus-within/folder:pointer-events-auto';

const ITEM_HIT =
  'pointer-events-none inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md group-hover/item:pointer-events-auto group-focus-within/item:pointer-events-auto';

const FOLDER_COUNT =
  'text-xs text-gray-500 transition-opacity duration-150 ease-in-out motion-reduce:transition-none group-hover/folder:opacity-0 group-focus-within/folder:opacity-0';

type RowDrag = { kind: 'folder' | 'item'; id: string };

function paintDragGhost(event: DragEvent<HTMLElement>) {
  const row = event.currentTarget.closest('.group\\/item, .group\\/folder');
  if (!(row instanceof HTMLElement)) return;
  const rect = row.getBoundingClientRect();
  const ghost = row.cloneNode(true) as HTMLElement;
  ghost.querySelectorAll('.absolute').forEach((node) => node.remove());
  ghost.style.position = 'fixed';
  ghost.style.left = `${rect.left}px`;
  ghost.style.top = `${rect.top}px`;
  ghost.style.width = `${rect.width}px`;
  ghost.style.margin = '0';
  ghost.style.opacity = '1';
  ghost.style.pointerEvents = 'none';
  ghost.style.backgroundColor = '#fff';
  ghost.style.zIndex = '80';
  ghost.style.boxSizing = 'border-box';
  document.body.appendChild(ghost);
  void ghost.offsetWidth;
  event.dataTransfer.setDragImage(
    ghost,
    Math.min(rect.width, Math.max(0, event.clientX - rect.left)),
    Math.min(rect.height, Math.max(0, event.clientY - rect.top))
  );
  window.setTimeout(() => ghost.remove(), 0);
}

function moveRow(ids: string[], fromId: string, toId: string): string[] | null {
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return null;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function savedHubPath(slug: string, hubs: Hub[]) {
  const known = hubs.find((hub) => hub.slug === slug);
  return known ? hubPlaceHref(known) : `/toolkits/${slug}`;
}

function SavedFolders() {
  const { folders, placeItem, renameFolder, reorderFolders } = useSavedItems();
  const location = useLocation();
  const fromState = { from: capturePageFrom(location) };
  const [hubCatalog, setHubCatalog] = useState<Hub[]>([]);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [draggingUi, setDraggingUi] = useState<RowDrag | null>(null);
  const [dropTarget, setDropTarget] = useState<RowDrag | null>(null);
  const skipBlurCommit = useRef(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const dragging = useRef<RowDrag | null>(null);
  const swallowClick = useRef(false);
  const visibleFolders = folders.length > 0 ? folders : [placeholderDefaultFolder()];
  const realFolderCount = visibleFolders.filter(
    (folder) => folder.id && folder.id !== LOCAL_DEFAULT_FOLDER_ID
  ).length;
  const canReorder =
    visibleFolders.length > 1 && visibleFolders.every((folder) => folder.id !== LOCAL_DEFAULT_FOLDER_ID);
  const savedPostIds = visibleFolders.flatMap((folder) =>
    folder.items.flatMap((item) => (item.type === 'RESOURCE' && item.postId != null ? [item.postId] : []))
  );
  const { byId: currency } = useResourceCurrency(savedPostIds);
  const needsHubs = visibleFolders.some((folder) => folder.items.some((item) => item.type === 'HUB'));

  useEffect(() => {
    if (!needsHubs) return;
    let cancelled = false;
    hubAPI
      .fetchHubs()
      .then((list) => {
        if (!cancelled) setHubCatalog(list);
      })
      .catch(() => {
        if (!cancelled) setHubCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [needsHubs]);

  useEffect(() => {
    if (!editingId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [editingId]);

  const canRename = (folder: SavedFolder) => Boolean(folder.id) && folder.id !== LOCAL_DEFAULT_FOLDER_ID;

  const cancelRename = () => {
    skipBlurCommit.current = true;
    setEditingId(null);
    setDraftName('');
    setRenameError(null);
  };

  const startRename = (folder: SavedFolder) => {
    if (!canRename(folder)) return;
    skipBlurCommit.current = false;
    setEditingId(folder.id);
    setDraftName(folder.name);
    setRenameError(null);
  };

  const commitRename = async (folder: SavedFolder) => {
    const name = draftName.trim().replace(/\s+/g, ' ');
    if (!name || name === folder.name) {
      cancelRename();
      return;
    }
    try {
      setRenaming(true);
      setRenameError(null);
      await renameFolder(folder.id, name);
      cancelRename();
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Failed to rename folder');
      renameInputRef.current?.focus();
    } finally {
      setRenaming(false);
    }
  };

  const toggle = (folder: SavedFolder) => {
    const bucket = folder.isDefault ? setCollapsedIds : setExpandedIds;
    bucket((current) => {
      const next = new Set(current);
      if (next.has(folder.id)) next.delete(folder.id);
      else next.add(folder.id);
      return next;
    });
  };

  const openFolder = (folder: SavedFolder) => {
    if (folder.isDefault) {
      setCollapsedIds((current) => {
        if (!current.has(folder.id)) return current;
        const next = new Set(current);
        next.delete(folder.id);
        return next;
      });
      return;
    }
    setExpandedIds((current) => {
      if (current.has(folder.id)) return current;
      const next = new Set(current);
      next.add(folder.id);
      return next;
    });
  };

  const clearDrag = () => {
    dragging.current = null;
    setDraggingUi(null);
    setDropTarget(null);
    window.setTimeout(() => {
      swallowClick.current = false;
    }, 0);
  };

  const beginDrag = (event: DragEvent<HTMLElement>, payload: RowDrag) => {
    swallowClick.current = true;
    dragging.current = payload;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', payload.id);
    paintDragGhost(event);
    setDraggingUi(payload);
  };

  const rememberDrop = (payload: RowDrag) => {
    setDropTarget((current) =>
      current?.kind === payload.kind && current.id === payload.id ? current : payload
    );
  };

  const placeDraggedItem = async (itemId: string, folderId: string, index: number) => {
    const folder = visibleFolders.find((entry) => entry.id === folderId);
    if (folder) openFolder(folder);
    try {
      await placeItem(itemId, folderId, index);
    } catch (error) {
      console.error('Failed to move saved item:', error);
    }
  };

  return (
    <div
      className="space-y-1"
      onClickCapture={(event) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {visibleFolders.map((folder) => {
        const isOpen = folder.isDefault ? !collapsedIds.has(folder.id) : expandedIds.has(folder.id);
        const folderDomId = `draft-folder-${folder.id || LOCAL_DEFAULT_FOLDER_ID}`;
        const showActions = canRename(folder) || canReorder;
        const folderDragging = draggingUi?.kind === 'folder' && draggingUi.id === folder.id;
        const folderDrop = dropTarget?.kind === 'folder' && dropTarget.id === folder.id;
        const editing = editingId === folder.id;
        return (
          <div
            key={folder.id || LOCAL_DEFAULT_FOLDER_ID}
            onDragOver={(event) => {
              const drag = dragging.current;
              if (!drag || editingId) return;
              if (drag.kind === 'item' && event.target instanceof Element && event.target.closest('[data-saved-item]')) {
                return;
              }
              if (drag.kind === 'folder') {
                if (!canReorder || drag.id === folder.id) return;
              } else if (folder.id === LOCAL_DEFAULT_FOLDER_ID) {
                return;
              } else {
                const source = visibleFolders.find((entry) => entry.items.some((item) => item.id === drag.id));
                if (source?.id === folder.id && source.items[0]?.id === drag.id) return;
              }
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              rememberDrop({ kind: 'folder', id: folder.id });
            }}
            onDrop={(event) => {
              event.preventDefault();
              const drag = dragging.current;
              clearDrag();
              if (!drag || editingId) return;
              if (drag.kind === 'folder') {
                if (!canReorder || drag.id === folder.id) return;
                const next = moveRow(
                  visibleFolders.map((entry) => entry.id),
                  drag.id,
                  folder.id
                );
                if (!next) return;
                void reorderFolders(next).catch((error) => {
                  console.error('Failed to reorder folders:', error);
                });
                return;
              }
              if (folder.id === LOCAL_DEFAULT_FOLDER_ID) return;
              void placeDraggedItem(drag.id, folder.id, 0);
            }}
            onDragLeave={(event) => {
              if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
              setDropTarget((current) =>
                current?.kind === 'folder' && current.id === folder.id ? null : current
              );
            }}
          >
            <div
              className={`group/folder relative -mx-2 rounded-md hover:bg-gray-50 focus-within:bg-gray-50 ${
                folderDrop ? 'ring-2 ring-primary' : ''
              } ${folderDragging ? 'opacity-50' : ''}`}
            >
              {editing ? (
                <div className="flex items-center gap-2 min-h-[44px] px-2">
                  <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-150 ease-in-out motion-reduce:transition-none ${
                      isOpen ? '' : '-rotate-90'
                    }`}
                    aria-hidden
                  />
                  <FolderIcon className="h-4 w-4 flex-shrink-0 text-accent" aria-hidden />
                  <input
                    ref={renameInputRef}
                    type="text"
                    value={draftName}
                    maxLength={60}
                    disabled={renaming}
                    aria-label="Folder name"
                    aria-invalid={Boolean(renameError)}
                    className="flex-1 min-w-0 bg-white border border-gray-300 rounded-md px-2 py-1 text-sm font-medium text-gray-900 focus:border-primary focus:ring-2 focus:ring-primary"
                    onChange={(event) => setDraftName(event.target.value)}
                    onBlur={() => {
                      if (skipBlurCommit.current) {
                        skipBlurCommit.current = false;
                        return;
                      }
                      void commitRename(folder);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void commitRename(folder);
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        skipBlurCommit.current = true;
                        cancelRename();
                      }
                    }}
                  />
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => toggle(folder)}
                    className="flex w-full items-center gap-2 min-h-[44px] px-2 rounded-md text-left focus-ring"
                    aria-expanded={isOpen}
                    aria-controls={folderDomId}
                    aria-label={folder.name}
                  >
                    <ChevronDownIcon
                      className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-150 ease-in-out motion-reduce:transition-none ${
                        isOpen ? '' : '-rotate-90'
                      }`}
                      aria-hidden
                    />
                    <FolderIcon className="h-4 w-4 flex-shrink-0 text-accent" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{folder.name}</span>
                    <span className={showActions ? FOLDER_COUNT : 'text-xs text-gray-500'} aria-hidden>
                      {folder.items.length}
                    </span>
                  </button>
                  {showActions ? (
                    <div className={`${FOLDER_CLUSTER} ${folderDragging ? '!opacity-100' : ''}`}>
                      {canRename(folder) ? (
                        <button
                          type="button"
                          onClick={() => startRename(folder)}
                          className={`${FOLDER_HIT} text-gray-500 hover:text-primary focus-ring ${
                            folderDragging ? '!pointer-events-auto' : ''
                          }`}
                          aria-label={`Rename ${folder.name}`}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                      {canReorder ? (
                        <span
                          draggable
                          onDragStart={(event) => beginDrag(event, { kind: 'folder', id: folder.id })}
                          onDragEnd={clearDrag}
                          className={`${FOLDER_HIT} text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing ${
                            folderDragging ? '!pointer-events-auto' : ''
                          }`}
                          aria-label={`Drag to reorder ${folder.name}`}
                        >
                          <Bars2Icon className="h-5 w-5" />
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
            {editing && renameError ? <p className="px-2 pt-1 text-sm text-error">{renameError}</p> : null}
            {isOpen && (
              <div id={folderDomId} className="pl-6">
                {folder.items.length === 0 ? (
                  <QuietLine>
                    {folder.isDefault
                      ? 'Saved resources and toolkits will appear here.'
                      : 'No items in this folder yet.'}
                  </QuietLine>
                ) : (
                  <ul>
                    {folder.items.map((item) => {
                      const live =
                        item.type === 'RESOURCE' && item.postId != null ? currency[item.postId] : undefined;
                      const title = stripHtml(live?.title || item.title);
                      const updated = live && !live.underRevision && live.modified ? parseISO(live.modified) : null;
                      const canDragItem =
                        folder.id !== LOCAL_DEFAULT_FOLDER_ID &&
                        (folder.items.length > 1 || realFolderCount > 1);
                      const itemDragging = draggingUi?.kind === 'item' && draggingUi.id === item.id;
                      const itemDrop = dropTarget?.kind === 'item' && dropTarget.id === item.id;
                      const body = (
                        <>
                          <ItemIcon item={item} />
                          <span className="min-w-0">
                            <span className="line-clamp-2">{title}</span>
                            {live?.underRevision && <RevisionNotice className="mt-1" />}
                            {updated && !Number.isNaN(updated.getTime()) && (
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Updated {format(updated, 'MMMM d, yyyy')}
                              </span>
                            )}
                          </span>
                        </>
                      );
                      const link =
                        item.type === 'HUB' ? (
                          <PlaceLink
                            to={savedHubPath(item.slug, hubCatalog)}
                            surface={{ kind: 'hub', slug: item.slug, via: 'saved' }}
                            className={SAVED_ITEM_ROW}
                            draggable={false}
                          >
                            {body}
                          </PlaceLink>
                        ) : item.type === 'PROGRAM' ? (
                          <PlaceLink
                            to={`/${item.slug}`}
                            surface={{ kind: 'program', slug: item.slug, via: 'saved' }}
                            className={SAVED_ITEM_ROW}
                            draggable={false}
                          >
                            {body}
                          </PlaceLink>
                        ) : (
                          <Link
                            to={buildResourcePath(item.slug)}
                            state={fromState}
                            draggable={false}
                            className={SAVED_ITEM_ROW}
                          >
                            {body}
                          </Link>
                        );
                      return (
                        <li
                          key={item.id}
                          data-saved-item
                          className={`group/item relative -mr-2 rounded-md hover:bg-gray-50 focus-within:bg-gray-50 ${
                            itemDrop ? 'ring-2 ring-primary' : ''
                          } ${itemDragging ? 'opacity-50' : ''}`}
                          onDragOver={(event) => {
                            const drag = dragging.current;
                            if (!drag || drag.kind !== 'item' || drag.id === item.id) return;
                            event.preventDefault();
                            event.stopPropagation();
                            event.dataTransfer.dropEffect = 'move';
                            rememberDrop({ kind: 'item', id: item.id });
                          }}
                          onDrop={(event) => {
                            const drag = dragging.current;
                            if (!drag || drag.kind !== 'item') return;
                            event.preventDefault();
                            event.stopPropagation();
                            clearDrag();
                            if (drag.id === item.id || folder.id === LOCAL_DEFAULT_FOLDER_ID) return;
                            const source = visibleFolders.find((entry) =>
                              entry.items.some((entryItem) => entryItem.id === drag.id)
                            );
                            if (!source) return;
                            if (source.id === folder.id) {
                              const next = moveRow(
                                folder.items.map((entry) => entry.id),
                                drag.id,
                                item.id
                              );
                              if (!next) return;
                              void placeDraggedItem(drag.id, folder.id, next.indexOf(drag.id));
                              return;
                            }
                            const index = folder.items.findIndex((entry) => entry.id === item.id);
                            if (index < 0) return;
                            void placeDraggedItem(drag.id, folder.id, index);
                          }}
                          onDragLeave={(event) => {
                            if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                            setDropTarget((current) =>
                              current?.kind === 'item' && current.id === item.id ? null : current
                            );
                          }}
                        >
                          {link}
                          {canDragItem ? (
                            <div className={`${ITEM_CLUSTER} ${itemDragging ? '!opacity-100' : ''}`}>
                              <span
                                draggable
                                onDragStart={(event) => beginDrag(event, { kind: 'item', id: item.id })}
                                onDragEnd={clearDrag}
                                className={`${ITEM_HIT} text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing ${
                                  itemDragging ? '!pointer-events-auto' : ''
                                }`}
                                aria-label={`Drag to move ${title}`}
                              >
                                <Bars2Icon className="h-5 w-5" />
                              </span>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function YourCourses() {
  const location = useLocation();
  const [enrollments, setEnrollments] = useState<Enrollment[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    elearningAPI
      .getUserEnrollments()
      .then((rows) => {
        if (cancelled) return;
        setFailed(false);
        setEnrollments(rows);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const courses = (enrollments ?? []).filter(
    (enrollment) =>
      enrollment.status !== EnrollmentStatus.CANCELLED &&
      Boolean(enrollment.course?.slug && enrollment.course.title)
  );

  if (failed) return <QuietLine>Courses could not be loaded.</QuietLine>;
  if (enrollments === null) return <QuietLine>Loading courses…</QuietLine>;
  if (courses.length === 0) return <QuietLine>Courses you enroll in will appear here.</QuietLine>;

  return (
    <ul className="space-y-1">
      {courses.map((enrollment) => {
        const course = enrollment.course;
        if (!course?.slug || !course.title) return null;
        const face = courseFace(course, enrollment);
        const status = courseStatus(face.stage);
        const fact = courseFact(course, face.stage, face.finished, face.progress);
        return (
          <li key={enrollment.id}>
            <Link
              to={`/elearning/courses/${course.slug}`}
              className="block min-h-[44px] px-2 -mx-2 py-2 rounded-md hover:bg-gray-50 focus-ring"
            >
              <p className="text-sm font-medium text-gray-900 line-clamp-2">{course.title}</p>
              <p className="text-sm text-gray-600">
                <span className={status.className}>{status.label}</span>
                {fact ? ` · ${fact}` : ''}
              </p>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function RecentlyViewed() {
  const location = useLocation();
  const posts = useRecentResources(RECENT_LIMIT);

  if (posts.length === 0) {
    return <QuietLine>Resources you view will appear here.</QuietLine>;
  }

  return (
    <PanelSection title="Recently viewed" icon={ClockIcon}>
      <ul>
        {posts.map((post) => (
          <li key={post.id}>
            <Link
              to={buildResourcePath(post.slug)}
              state={{ from: capturePageFrom(location) }}
              className={LIST_ROW}
            >
              <ContentTypeIcon id={post.id} title={post.title.rendered} />
              <span className="min-w-0">
                <span className="line-clamp-2">{decodeHtmlEntities(post.title.rendered)}</span>
                {isUnderRevision(post.acf?.under_revision) && <RevisionNotice className="mt-1" />}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </PanelSection>
  );
}

export default function MyOrbconPanel() {
  const { isOpen, setOpen } = useUserSidebar();
  const { user } = useUser();
  const location = useLocation();
  const extras = readProfileExtras(user?.unsafeMetadata);
  const greetingName = formatUserDisplayName({
    prefix: extras.namePrefix,
    firstName: user?.firstName,
  });

  return (
    <aside
      id={MY_ORBCON_PANEL_ID}
      className={`
        fixed right-0 top-0 bottom-0 z-40 w-80 max-w-full
        flex flex-col bg-white border-l border-gray-200
        transition-transform duration-150 ease-in-out motion-reduce:transition-none
        print:hidden
        ${isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}
      `}
      aria-label={MY_ORBCON_LABEL}
      aria-hidden={!isOpen}
      inert={!isOpen}
    >
      <div className="flex-shrink-0 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2 px-4 h-24">
          <div className="flex items-start gap-2 min-w-0">
            <BloodDropIcon className="h-5 w-5 flex-shrink-0 text-accent" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">{MY_ORBCON_LABEL}</p>
              {greetingName && greetingName !== 'Account' ? (
                <p className="text-sm text-gray-600 truncate">Hi, {greetingName}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-gray-500 hover:text-gray-700 focus-ring rounded-md"
            aria-label={`Close ${MY_ORBCON_LABEL}`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-6 space-y-6">
        {location.pathname !== '/' && (
          <Link
            to="/"
            className="inline-flex items-center gap-2 min-h-[44px] text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-md"
          >
            <Squares2X2Icon className="h-5 w-5 flex-shrink-0" />
            Go to my home
          </Link>
        )}

        <PanelSection title="Saved for later" icon={BookmarkIcon}>
          <SavedFolders />
        </PanelSection>

        <PanelSection title="Your courses" icon={AcademicCapIcon}>
          <YourCourses />
        </PanelSection>

        <RecentlyViewed />
      </div>
    </aside>
  );
}
